import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import {
  useGetAllHeroBannersQuery,
} from "@/store/api/homepageApi";
import HeroBannerForm from "./HeroBannerForm";

const HeroBannerManagement = () => {
  const [formOpen, setFormOpen] = useState(false);

  const { data, isLoading, refetch } = useGetAllHeroBannersQuery({ limit: 1 });

  const banner = data?.data?.[0] ?? data?.[0] ?? null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Hero Banner
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The main banner displayed at the top of the homepage.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={banner ? <EditIcon /> : <AddIcon />}
          onClick={() => setFormOpen(true)}
          disabled={isLoading}
        >
          {banner ? "Edit Banner" : "Create Banner"}
        </Button>
      </Box>

      {isLoading ? (
        <Skeleton variant="rectangular" sx={{ width: "100%", height: 240, borderRadius: 3 }} />
      ) : banner ? (
        <Paper
          sx={{ borderRadius: 3, overflow: "hidden", cursor: "pointer" }}
          onClick={() => setFormOpen(true)}
        >
          {/* Image preview */}
          {banner.image ? (
            <Box
              component="img"
              src={banner.image}
              alt={banner.title}
              sx={{ width: "100%", maxHeight: 300, objectFit: "cover", display: "block" }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: 200,
                background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ImageIcon sx={{ fontSize: 64, color: "rgba(255,255,255,0.2)" }} />
            </Box>
          )}

          {/* Info row */}
          <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {banner.title}
              </Typography>
              {banner.description && (
                <Typography variant="body2" color="text.secondary">
                  {banner.description}
                </Typography>
              )}
            </Box>
            <Chip
              label={banner.isActive ? "Active" : "Inactive"}
              size="small"
              color={banner.isActive ? "success" : "default"}
              sx={{ flexShrink: 0, mt: 0.25 }}
            />
          </Box>
        </Paper>
      ) : (
        <Paper
          sx={{
            borderRadius: 3,
            border: "2px dashed rgba(0,0,0,0.12)",
            py: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            color: "text.secondary",
            cursor: "pointer",
            "&:hover": { borderColor: "primary.main", color: "primary.main" },
          }}
          onClick={() => setFormOpen(true)}
        >
          <ImageIcon sx={{ fontSize: 48, opacity: 0.4 }} />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            No banner yet
          </Typography>
          <Typography variant="body2">Click to create the hero banner</Typography>
        </Paper>
      )}

      <HeroBannerForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        banner={banner}
        onSuccess={() => refetch()}
      />
    </Box>
  );
};

export default HeroBannerManagement;
